import React from 'react';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { authFormBlur } from '../../../utils/auth-form-navigation';
import { MagicLinkIntentEnum } from '../../../defs/api';
import { useRootStore } from '../../../providers/root-store-provider';
import { TextField } from '../../app/form/textfield/textfield';
import { FormItem } from '../../app/form/item/item';
import { WebButton as Button } from '../button/web-button';
import { Alert } from '../../app/alert/alert';
import { Agree } from '../dialogs/account/agree/agree';
import { AgreeTerms } from '../dialogs/account/agree/terms/agree-terms';
import { AgreeMarketing } from '../dialogs/account/agree/marketing/agree-marketing';
import { ConsentRequiredAction } from '../dialogs/account/consent-required-action';

interface MagicLinkFormProps {
    intent: MagicLinkIntentEnum;
}

interface MagicLinkFormValues {
    email: string;
}

export const MagicLinkForm: React.FC<MagicLinkFormProps> = observer(({ intent }) => {
    const { t } = useTranslation();
    const { authStore, alertStore } = useRootStore();
    const { authDialogStore } = authStore;
    const { agreedTerms, loading, magicLinkRequested } = authDialogStore;
    const isSignUp = intent === MagicLinkIntentEnum.SignUp;
    const submitLabel = t(isSignUp ? 'dialogs.magicLink.sendSignUp' : 'dialogs.magicLink.sendLogin');

    const validationSchema = yup.object({
        email: yup
            .string()
            .required(t('layout.forms.common.emailValidationRequired'))
            .email(t('layout.forms.common.emailValidationValid')),
    });

    const submit = async ({ email }: MagicLinkFormValues): Promise<void> => {
        if (isSignUp && !authDialogStore.agreedTerms) {
            alertStore.setErrorMessage(t('layout.forms.common.agreeTermsValidation'));
            return;
        }

        await authStore.requestMagicLink(email, intent);
    };

    return (
        <Formik<MagicLinkFormValues>
            initialValues={{ email: '' }}
            validationSchema={validationSchema}
            onSubmit={submit}
        >
            {({ values, handleChange, touched, handleBlur, errors, handleSubmit }) => (
                <Form id={`magic-link-${intent}-form`}>
                    {magicLinkRequested && (
                        <Alert type="success" icon>
                            {t('dialogs.magicLink.requested')}
                        </Alert>
                    )}

                    <FormItem>
                        <TextField
                            appearance="web"
                            fullWidth
                            label={t('layout.forms.common.emailField')}
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={authFormBlur(handleBlur)}
                            helperText={touched.email && errors.email ? errors.email : ''}
                            error={Boolean(touched.email && errors.email)}
                        />
                    </FormItem>

                    {isSignUp && (
                        <FormItem>
                            <Agree>
                                <AgreeTerms />
                                <AgreeMarketing />
                            </Agree>
                        </FormItem>
                    )}

                    <FormItem>
                        {isSignUp && !agreedTerms ? (
                            <ConsentRequiredAction>
                                <Button
                                    size="large"
                                    block
                                    variant="primary"
                                    loading={loading}
                                    disabled
                                    onClick={(event) => {
                                        event.preventDefault();
                                        alertStore.setErrorMessage(t('layout.forms.common.agreeTermsValidation'));
                                    }}
                                >
                                    {submitLabel}
                                </Button>
                            </ConsentRequiredAction>
                        ) : (
                            <Button
                                size="large"
                                block
                                variant="primary"
                                loading={loading}
                                onClick={(event) => {
                                    event.preventDefault();
                                    handleSubmit();
                                }}
                            >
                                {submitLabel}
                            </Button>
                        )}
                    </FormItem>
                </Form>
            )}
        </Formik>
    );
});
